import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Checkbox } from './ui/checkbox';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from './ui/select';
import { Input } from './ui/input';
import { 
  Clock, Calendar, Image, ShoppingCart, Info,
  CheckCircle2, AlertCircle, Loader2, Plus, Minus,
  Upload, File, X, FileImage, FileText
} from 'lucide-react';

/**
 * FileUploadField Component
 * Allows customers to upload files for product customization
 */
const FileUploadField = ({ field, value, onChange }) => {
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const fileInputRef = React.useRef(null);
  
  // Get allowed file types from field config or use defaults
  const allowedTypes = field.allowed_types || ['image/*', '.pdf', '.doc', '.docx'];
  const maxSize = field.max_size || 10; // MB
  const maxSizeBytes = maxSize * 1024 * 1024;
  
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setError(null);
    
    // Validate file size
    if (file.size > maxSizeBytes) {
      setError(`File size must be less than ${maxSize}MB`);
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const token = sessionStorage.getItem('token');
      
      const response = await fetch(`${BACKEND_URL}/api/files/upload`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Upload failed');
      }
      
      const data = await response.json();
      
      // Store file info
      onChange({
        url: data.url,
        filename: file.name,
        size: file.size,
        type: file.type
      });
      
    } catch (err) {
      console.error('File upload error:', err);
      setError('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };
  
  const handleRemove = () => {
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const getFileIcon = (type) => {
    if (type?.startsWith('image/')) return FileImage;
    return FileText;
  };
  
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  
  return (
    <div className="space-y-2">
      <input
        ref={fileInputRef}
        type="file"
        accept={allowedTypes.join(',')}
        onChange={handleFileSelect}
        className="hidden"
        id={`file-upload-${field.id}`}
      />
      
      {value?.url ? (
        // File uploaded - show preview
        <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg max-w-md">
          {value.type?.startsWith('image/') ? (
            <img 
              src={value.url.startsWith('http') ? value.url : `${process.env.REACT_APP_BACKEND_URL}${value.url}`}
              alt={value.filename}
              className="w-12 h-12 object-cover rounded"
            />
          ) : (
            <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
              {React.createElement(getFileIcon(value.type), { className: "h-6 w-6 text-gray-500" })}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{value.filename}</p>
            <p className="text-xs text-gray-500">{formatFileSize(value.size)}</p>
          </div>
          <button
            type="button"
            onClick={handleRemove}
            className="p-1 text-gray-400 hover:text-red-500 transition-colors"
            title="Remove file"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      ) : (
        // No file - show upload button
        <label
          htmlFor={`file-upload-${field.id}`}
          className={`flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-lg cursor-pointer transition-colors max-w-md ${
            uploading 
              ? 'border-blue-300 bg-blue-50' 
              : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
          }`}
        >
          {uploading ? (
            <>
              <Loader2 className="h-8 w-8 text-blue-500 animate-spin mb-2" />
              <span className="text-sm text-blue-600">Uploading...</span>
            </>
          ) : (
            <>
              <Upload className="h-8 w-8 text-gray-400 mb-2" />
              <span className="text-sm font-medium text-gray-700">Click to upload</span>
              <span className="text-xs text-gray-500 mt-1">
                Max {maxSize}MB • {field.allowed_types_label || 'Images, PDF, Documents'}
              </span>
            </>
          )}
        </label>
      )}
      
      {error && (
        <p className="text-sm text-red-600 flex items-center gap-1">
          <AlertCircle className="h-4 w-4" />
          {error}
        </p>
      )}
      
      {field.help_text && !error && (
        <p className="text-xs text-gray-500">{field.help_text}</p>
      )}
    </div>
  );
};

/**
 * ProductOptionsDisplay Component
 * Renders the product customization options for customers to select.
 * Calculates price based on selections and shows appropriate messages.
 * 
 * @param {string} listingId - The listing ID to fetch options for
 * @param {number} basePrice - The base price of the product
 * @param {object} variantSelections - Current variant selections with prices
 * @param {number} variantPriceAdjustment - Total price adjustment from variants
 * @param {function} onOptionsChange - Callback when options change
 * @param {function} onPriceChange - Callback when calculated price changes
 */
const ProductOptionsDisplay = ({ 
  listingId, 
  basePrice = 0,
  variantSelections = {},
  variantPriceAdjustment = 0,
  onOptionsChange,
  onPriceChange
}) => {
  const [optionsData, setOptionsData] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [priceBreakdown, setPriceBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);

  // Fetch options configuration
  useEffect(() => {
    if (listingId) {
      fetchOptions();
    }
  }, [listingId]);

  const fetchOptions = async () => {
    setLoading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      // First try the template-based API
      const response = await fetch(`${BACKEND_URL}/api/listing-options/listing/${listingId}`);
      const data = await response.json();
      
      if (data.has_options && data.template) {
        setOptionsData(data);
        // Initialize default selections
        initializeDefaults(data.template);
      } else {
        // Fallback: Fetch listing and check for inline custom options
        const token = sessionStorage.getItem('token');
        const listingResponse = await fetch(`${BACKEND_URL}/api/listings/${listingId}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        
        if (listingResponse.ok) {
          const listingData = await listingResponse.json();
          
          // Check for inline custom options in data field
          const customConfig = listingData.data?.customOptionsConfig || listingData.data?.productOptionsConfig;
          
          if (customConfig?.options?.length > 0) {
            // Transform inline options to template format
            const inlineTemplate = {
              steps: [{
                title: 'Customize Your Product',
                fields: customConfig.options.map(opt => ({
                  id: opt.id,
                  label: opt.name,
                  type: opt.type || 'radio',
                  help_text: opt.helpText || '',
                  required: opt.required || false,
                  default_mechanism: opt.mechanism || 'addon',
                  options: (opt.choices || []).map(choice => ({
                    label: choice.label,
                    value: choice.value || choice.label?.toLowerCase().replace(/\s+/g, '_'),
                    default_price: choice.price || 0
                  }))
                }))
              }]
            };
            
            setOptionsData({
              has_options: true,
              template: inlineTemplate,
              config: {
                field_overrides: customConfig.options.map(opt => ({
                  field_id: opt.id,
                  mechanism: opt.mechanism || 'addon',
                  options_pricing: (opt.choices || []).reduce((acc, choice) => {
                    acc[choice.value || choice.label?.toLowerCase().replace(/\s+/g, '_')] = choice.price || 0;
                    return acc;
                  }, {})
                }))
              }
            });
            
            // Initialize defaults for inline options
            initializeDefaults(inlineTemplate);
          } else {
            setOptionsData(null);
          }
        } else {
          setOptionsData(null);
        }
      }
    } catch (error) {
      console.error('Failed to fetch product options:', error);
      setOptionsData(null);
    } finally {
      setLoading(false);
    }
  };

  const initializeDefaults = (template) => {
    const defaults = {};
    template.steps?.forEach(step => {
      step.fields?.forEach(field => {
        if (field.required && field.options?.length > 0) {
          // Set first option as default for required fields
          defaults[field.id] = field.options[0].value;
        }
      });
    });
    setSelectedOptions(defaults);
  };

  // Calculate price when options change
  const calculatePrice = useCallback(async () => {
    if (!optionsData || Object.keys(selectedOptions).length === 0) {
      setPriceBreakdown(null);
      return;
    }

    setCalculating(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/listing-options/calculate-price`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          listing_id: listingId,
          selected_options: selectedOptions
        })
      });
      const data = await response.json();
      setPriceBreakdown(data);
      
      if (onPriceChange) {
        onPriceChange(data.final_price);
      }
    } catch (error) {
      console.error('Failed to calculate price:', error);
    } finally {
      setCalculating(false);
    }
  }, [listingId, selectedOptions, optionsData, onPriceChange]);

  useEffect(() => {
    const timer = setTimeout(() => {
      calculatePrice();
    }, 300);
    return () => clearTimeout(timer);
  }, [calculatePrice]);

  // Notify parent of option changes
  useEffect(() => {
    if (onOptionsChange) {
      onOptionsChange(selectedOptions);
    }
  }, [selectedOptions, onOptionsChange]);

  const handleOptionChange = (fieldId, value) => {
    setSelectedOptions(prev => {
      // If clicking the same value, deselect it (for optional add-ons)
      if (prev[fieldId] === value) {
        const newState = { ...prev };
        delete newState[fieldId];
        return newState;
      }
      return {
        ...prev,
        [fieldId]: value
      };
    });
  };

  const handleCheckboxChange = (fieldId, optionValue, checked) => {
    setSelectedOptions(prev => {
      const current = prev[fieldId] || [];
      if (checked) {
        return { ...prev, [fieldId]: [...current, optionValue] };
      } else {
        return { ...prev, [fieldId]: current.filter(v => v !== optionValue) };
      }
    });
  };

  // Calculate local price for inline options (when API doesn't have data)
  const calculateLocalPrice = useCallback(() => {
    if (!optionsData?.config?.field_overrides) return 0;
    
    let total = 0;
    
    Object.entries(selectedOptions).forEach(([fieldId, value]) => {
      const fieldOverride = optionsData.config.field_overrides.find(o => o.field_id === fieldId);
      if (fieldOverride?.options_pricing) {
        if (Array.isArray(value)) {
          // Checkbox - sum all selected
          value.forEach(v => {
            total += fieldOverride.options_pricing[v] || 0;
          });
        } else {
          // Radio/dropdown - single value
          total += fieldOverride.options_pricing[value] || 0;
        }
      }
    });
    
    return total;
  }, [selectedOptions, optionsData]);

  // Render different field types
  const renderField = (field) => {
    // Get pricing overrides if available
    const fieldOverride = optionsData?.config?.field_overrides?.find(o => o.field_id === field.id);
    const isOptional = !field.required;
    const mechanism = fieldOverride?.mechanism || field.default_mechanism || 'addon';
    
    switch (field.type) {
      case 'radio':
        return (
          <div className="space-y-2">
            {field.options.map(option => {
              const price = fieldOverride?.options_pricing?.[option.value] || option.default_price || 0;
              const isSelected = selectedOptions[field.id] === option.value;
              return (
                <label
                  key={option.value}
                  onClick={() => handleOptionChange(field.id, option.value)}
                  className={`flex items-center justify-between p-3 border-2 rounded-xl cursor-pointer transition-all ${
                    isSelected 
                      ? 'border-blue-600 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      isSelected ? 'border-blue-600' : 'border-gray-300'
                    }`}>
                      {isSelected && (
                        <div className="w-2.5 h-2.5 rounded-full bg-blue-600" />
                      )}
                    </div>
                    <span className="font-medium">{option.label}</span>
                  </div>
                  {price > 0 && (
                    <span className={`text-sm font-semibold ${isSelected ? 'text-blue-600' : 'text-green-600'}`}>
                      +₹{price.toLocaleString()}
                    </span>
                  )}
                </label>
              );
            })}
            {isOptional && selectedOptions[field.id] && (
              <button
                type="button"
                onClick={() => {
                  setSelectedOptions(prev => {
                    const newState = { ...prev };
                    delete newState[field.id];
                    return newState;
                  });
                }}
                className="text-sm text-gray-500 hover:text-red-500 flex items-center gap-1"
              >
                <span>✕</span> Clear selection
              </button>
            )}
          </div>
        );

      case 'checkbox':
        return (
          <div className="space-y-2">
            {field.options.map(option => {
              const isChecked = (selectedOptions[field.id] || []).includes(option.value);
              const price = fieldOverride?.options_pricing?.[option.value] || option.default_price || 0;
              return (
                <label
                  key={option.value}
                  className={`flex items-center justify-between p-3 border-2 rounded-xl cursor-pointer transition-all ${
                    isChecked ? 'border-green-600 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={isChecked}
                      onCheckedChange={(checked) => handleCheckboxChange(field.id, option.value, checked)}
                      className="h-5 w-5"
                    />
                    <span className="font-medium">{option.label}</span>
                  </div>
                  {price > 0 && (
                    <span className={`text-sm font-semibold ${isChecked ? 'text-green-600' : 'text-green-600'}`}>
                      +₹{price.toLocaleString()}
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        );

      case 'dropdown':
        return (
          <Select
            value={selectedOptions[field.id] || ''}
            onValueChange={(value) => handleOptionChange(field.id, value)}
          >
            <SelectTrigger className="w-full max-w-xs">
              <SelectValue placeholder={`Select ${field.label}`} />
            </SelectTrigger>
            <SelectContent>
              {field.options.map(option => {
                const price = fieldOverride?.options_pricing?.[option.value] || option.default_price || 0;
                return (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex items-center justify-between gap-4">
                      <span>{option.label}</span>
                      {price > 0 && (
                        <span className="text-green-600 font-semibold">+₹{price.toLocaleString()}</span>
                      )}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        );

      case 'text':
        return (
          <Input
            type="text"
            placeholder={field.placeholder || `Enter ${field.label}`}
            value={selectedOptions[field.id] || ''}
            onChange={(e) => handleOptionChange(field.id, e.target.value)}
            className="max-w-md"
          />
        );

      case 'number':
        return (
          <div className="flex items-center gap-2 max-w-xs">
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                const current = parseInt(selectedOptions[field.id] || '1');
                if (current > 1) handleOptionChange(field.id, String(current - 1));
              }}
            >
              <Minus className="h-4 w-4" />
            </Button>
            <Input
              type="number"
              min="1"
              value={selectedOptions[field.id] || '1'}
              onChange={(e) => handleOptionChange(field.id, e.target.value)}
              className="w-20 text-center"
            />
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                const current = parseInt(selectedOptions[field.id] || '1');
                handleOptionChange(field.id, String(current + 1));
              }}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        );

      case 'date':
        return (
          <div className="relative max-w-xs">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="date"
              value={selectedOptions[field.id] || ''}
              onChange={(e) => handleOptionChange(field.id, e.target.value)}
              className="pl-10"
            />
          </div>
        );

      case 'time':
        return (
          <div className="relative max-w-xs">
            <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="time"
              value={selectedOptions[field.id] || ''}
              onChange={(e) => handleOptionChange(field.id, e.target.value)}
              className="pl-10"
            />
          </div>
        );

      case 'image_gallery':
        return (
          <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
            {field.options.map(option => {
              const isSelected = selectedOptions[field.id] === option.value;
              return (
                <button
                  key={option.value}
                  onClick={() => handleOptionChange(field.id, option.value)}
                  className={`relative aspect-square border-2 rounded-lg overflow-hidden transition-all ${
                    isSelected ? 'border-blue-600 ring-2 ring-blue-200' : 'border-gray-200'
                  }`}
                >
                  {option.image ? (
                    <img 
                      src={option.image} 
                      alt={option.label} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-100">
                      <Image className="h-8 w-8 text-gray-400" />
                    </div>
                  )}
                  {isSelected && (
                    <div className="absolute inset-0 bg-blue-600/20 flex items-center justify-center">
                      <CheckCircle2 className="h-6 w-6 text-blue-600" />
                    </div>
                  )}
                  <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-1 text-center truncate">
                    {option.label}
                  </span>
                </button>
              );
            })}
          </div>
        );

      case 'file_upload':
        return (
          <FileUploadField
            field={field}
            value={selectedOptions[field.id]}
            onChange={(fileData) => handleOptionChange(field.id, fileData)}
          />
        );

      default:
        return (
          <Input
            type="text"
            placeholder={field.placeholder || `Enter ${field.label}`}
            value={selectedOptions[field.id] || ''}
            onChange={(e) => handleOptionChange(field.id, e.target.value)}
            className="max-w-md"
          />
        );
    }
  };

  if (loading) {
    return (
      <Card className="animate-pulse">
        <CardContent className="p-6">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-8 bg-gray-200 rounded w-full mb-2"></div>
          <div className="h-8 bg-gray-200 rounded w-full"></div>
        </CardContent>
      </Card>
    );
  }

  if (!optionsData) {
    return null; // No options for this product
  }

  const template = optionsData.template;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Customize Your Order
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {template.steps?.map((step, stepIndex) => (
            <div key={step.id} className="space-y-4">
              {template.steps.length > 1 && (
                <h3 className="font-semibold flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-sm flex items-center justify-center">
                    {stepIndex + 1}
                  </span>
                  {step.name}
                </h3>
              )}
              
              {step.fields?.map(field => (
                <div key={field.id} className="space-y-2">
                  <Label className="flex items-center gap-2">
                    {field.label}
                    {field.required && <span className="text-red-500">*</span>}
                  </Label>
                  {field.help_text && (
                    <p className="text-sm text-gray-500">{field.help_text}</p>
                  )}
                  {renderField(field)}
                </div>
              ))}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Price Summary */}
      <Card className="bg-gray-50">
        <CardContent className="p-4">
          <h4 className="font-semibold mb-3 flex items-center gap-2">
            Price Summary
            {calculating && <Loader2 className="h-4 w-4 animate-spin" />}
          </h4>
          
          <div className="space-y-2">
            {/* Base Price */}
            <div className="flex justify-between text-sm">
              <span>Base Price</span>
              <span>₹{basePrice.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            
            {/* Variant Selections (from product variants like Color, Size) */}
            {Object.entries(variantSelections).map(([variantName, selection]) => {
              const priceImpact = selection.price || 0;
              if (priceImpact > 0) {
                return (
                  <div key={variantName} className="flex justify-between text-sm">
                    <span className="flex items-center gap-1">
                      <Badge variant="outline" className="text-xs">Variant</Badge>
                      {variantName}: {selection.optionName}
                    </span>
                    <span className="text-green-600">+₹{priceImpact.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                );
              }
              return null;
            })}
            
            {/* Custom Options / Add-ons Breakdown */}
            {Object.entries(selectedOptions).map(([fieldId, value]) => {
              if (!value || (Array.isArray(value) && value.length === 0)) return null;
              
              const fieldOverride = optionsData?.config?.field_overrides?.find(o => o.field_id === fieldId);
              const field = optionsData?.template?.steps?.flatMap(s => s.fields).find(f => f.id === fieldId);
              
              if (!fieldOverride?.options_pricing || !field) return null;
              
              // Handle array (checkbox) or single value (radio/dropdown)
              const values = Array.isArray(value) ? value : [value];
              
              return values.map((v, idx) => {
                const price = fieldOverride.options_pricing[v] || 0;
                if (price <= 0) return null;
                
                const optionLabel = field.options?.find(o => o.value === v)?.label || v;
                
                return (
                  <div key={`${fieldId}-${idx}`} className="flex justify-between text-sm">
                    <span className="flex items-center gap-1">
                      <Badge variant="outline" className="text-xs bg-green-50 text-green-700">Add-on</Badge>
                      {field.label}: {optionLabel}
                    </span>
                    <span className="text-green-600">+₹{price.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                );
              });
            })}
            
            {/* Legacy: Product Options Breakdown from API */}
            {priceBreakdown?.breakdown?.map((item, index) => {
              // For variant type options, only show if there's a price difference from base
              const priceImpact = item.type === 'variant' 
                ? item.amount - basePrice  // Show difference from base price
                : item.amount;
              
              // Skip if no price impact
              if (priceImpact === 0) return null;
              
              return (
                <div key={item.id || item.name || `${item.title || "item"}-${index}`} className="flex justify-between text-sm">
                  <span className="flex items-center gap-1">
                    {item.type === 'variant' ? (
                      <Badge variant="outline" className="text-xs">Option</Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs bg-green-50">Add-on</Badge>
                    )}
                    {item.label}
                  </span>
                  <span className="text-green-600">
                    +₹{Math.abs(priceImpact).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              );
            })}
            
            {/* Total */}
            <div className="border-t pt-2 mt-2 flex justify-between font-semibold text-lg">
              <span>Total</span>
              <span className="text-blue-600">
                ₹{(basePrice + variantPriceAdjustment + calculateLocalPrice()).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductOptionsDisplay;
